import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import { MessageModel } from '../types/models';
import { apiClient } from '../clients/APIClient';
import { useTTSStore } from './ttsStore';

export type MessageUpdater =
    | MessageModel[]
    | ((prev: MessageModel[]) => MessageModel[]);

type ChatHistoryStore = {
    chatHistory: MessageModel[];
    inputs: MessageModel[];
    chatId: string;
    selectedModel: string;
    isGenerating: boolean;
    inUseVLM: boolean;
    hasImages: boolean;
    setHistory: (updater: MessageUpdater) => void;
    setInputs: (updater: MessageUpdater) => void;
    setChatId: (chatId: string | ((prev: string) => string)) => void;
    setSelectedModel: (model: string | ((prev: string) => string)) => void;
    setIsGenerating: (isGenerating: boolean | ((prev: boolean) => boolean)) => void;
    setInUseVLM: (inUseVLM: boolean | ((prev: boolean) => boolean)) => void;
    setHasImages: (hasImages: boolean | ((prev: boolean) => boolean)) => void;
    checkIfChatContainsImage: (messages: MessageModel[]) => boolean;
    stopGeneration: () => Promise<void>;
    resetCurrentChatState: (nextChatId: string) => void;
};

const enableDevtools = process.env.NODE_ENV !== 'production';

export const useChatHistoryStore = create<ChatHistoryStore>()(
    devtools(
        (set, get) => ({
            chatHistory: [],
            inputs: [],
            chatId: uuidv4(),
            selectedModel: '',
            isGenerating: false,
            inUseVLM: false,
            hasImages: false,

            setHistory: (updater) =>
                set(
                    (state) => ({
                        chatHistory:
                            typeof updater === 'function'
                                ? updater(state.chatHistory)
                                : updater,
                    }),
                    false,
                    'chatHistory/setHistory',
                ),

            setInputs: (updater) =>
                set(
                    (state) => ({
                        inputs:
                            typeof updater === 'function'
                                ? updater(state.inputs)
                                : updater,
                    }),
                    false,
                    'chatHistory/setInputs',
                ),

            setChatId: (chatId) =>
                set(
                    (state) => ({
                        chatId:
                            typeof chatId === 'function'
                                ? chatId(state.chatId)
                                : chatId,
                    }),
                    false,
                    'chatHistory/setChatId',
                ),

            setSelectedModel: (model) =>
                set(
                    (state) => ({
                        selectedModel:
                            typeof model === 'function'
                                ? model(state.selectedModel)
                                : model,
                    }),
                    false,
                    'chatHistory/setSelectedModel',
                ),

            setIsGenerating: (isGenerating) =>
                set(
                    (state) => ({
                        isGenerating:
                            typeof isGenerating === 'function'
                                ? isGenerating(state.isGenerating)
                                : isGenerating,
                    }),
                    false,
                    'chatHistory/setIsGenerating',
                ),

            setInUseVLM: (inUseVLM) =>
                set(
                    (state) => ({
                        inUseVLM:
                            typeof inUseVLM === 'function'
                                ? inUseVLM(state.inUseVLM)
                                : inUseVLM,
                    }),
                    false,
                    'chatHistory/setInUseVLM',
                ),

            setHasImages: (hasImages) =>
                set(
                    (state) => ({
                        hasImages:
                            typeof hasImages === 'function'
                                ? hasImages(state.hasImages)
                                : hasImages,
                    }),
                    false,
                    'chatHistory/setHasImages',
                ),

            checkIfChatContainsImage: (messages) => {
                const checkImage = messages.some(
                    (message) => message.images && message.images.length > 0
                );
                return checkImage;
            },

            stopGeneration: async () => {
                try {
                    await Promise.allSettled([
                        apiClient.chat.stopGeneration(),
                        Promise.resolve(useTTSStore.getState().stop()),
                    ]);
                    get().setIsGenerating(false);
                } catch (err) {
                    console.error('Error stopping generation:', err);
                }
            },

            resetCurrentChatState: (nextChatId) => {
                set(
                    {
                        chatHistory: [],
                        inputs: [],
                        chatId: nextChatId,
                        isGenerating: false,
                        inUseVLM: false,
                    },
                    false,
                    'chatHistory/resetCurrentChatState',
                );
            },
        }),
        { enabled: enableDevtools, name: 'chat-history-store' },
    ),
);
